import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Modal, Field, inputCls, PrimaryBtn, GhostBtn, DangerBtn, DataTable } from "../components/DataTable";
import { toast } from "sonner";
import { Database, Trash2 } from "lucide-react";

const empty = { name: "", customer: "", location: "", description: "" };

export default function Sites() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  // Purge modal
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeSite, setPurgeSite] = useState(null);
  const [purgeStats, setPurgeStats] = useState(null);
  const [purgeOpts, setPurgeOpts] = useState({ readings: true, alarms: true, active_too: false, before: "" });
  const [purgeBusy, setPurgeBusy] = useState(false);

  const load = () => api.get("/sites").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/sites/${editId}`, form);
      else await api.post("/sites", form);
      toast.success(editId ? "Site updated" : "Site created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete site AND all its devices/tags/readings/alarms?")) return;
    await api.delete(`/sites/${id}`);
    toast.success("Deleted");
    load();
  };

  const openPurge = async (site) => {
    setPurgeSite(site);
    setPurgeOpts({ readings: true, alarms: true, active_too: false, before: "" });
    setPurgeStats(null);
    setPurgeOpen(true);
    try {
      const { data } = await api.get(`/sites/${site.id}/stats`);
      setPurgeStats(data);
    } catch {}
  };

  const doPurge = async () => {
    if (!confirm(`Purge data for "${purgeSite.name}"? This cannot be undone.`)) return;
    setPurgeBusy(true);
    try {
      const body = { ...purgeOpts };
      if (!body.before) delete body.before;
      const { data } = await api.post(`/sites/${purgeSite.id}/purge`, body);
      const del = data.deleted || {};
      toast.success(`Purged: readings ${del.readings ?? 0}, alarms ${del.alarms ?? 0}`);
      // Refresh stats
      const { data: s } = await api.get(`/sites/${purgeSite.id}/stats`);
      setPurgeStats(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurgeBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">CUSTOMER MANAGEMENT</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Sites</h1>
        </div>
        <PrimaryBtn onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-site-btn">+ Add Site</PrimaryBtn>
      </div>

      <DataTable
        testId="sites-table"
        columns={[
          { key: "name", label: "NAME" },
          { key: "customer", label: "CUSTOMER" },
          { key: "location", label: "LOCATION" },
          { key: "description", label: "DESC" },
        ]}
        rows={rows}
        actions={(r) => (
          <>
            <GhostBtn data-testid={`purge-site-${r.id}`} onClick={() => openPurge(r)}><Database className="inline mr-1" size={10} /> Purge</GhostBtn>
            <GhostBtn data-testid={`edit-site-${r.id}`} onClick={() => { setForm(r); setEditId(r.id); setOpen(true); }}>Edit</GhostBtn>
            <DangerBtn data-testid={`del-site-${r.id}`} onClick={() => remove(r.id)}>Del</DangerBtn>
          </>
        )}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Site" : "Create Site"} testId="site-modal">
        <form onSubmit={save}>
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls()} data-testid="site-name" /></Field>
          <Field label="Customer"><input required value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} className={inputCls()} data-testid="site-customer" /></Field>
          <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls()} /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls()} rows={3} /></Field>
          <div className="flex justify-end gap-2 mt-4"><PrimaryBtn type="submit" data-testid="save-site">Save</PrimaryBtn></div>
        </form>
      </Modal>

      <Modal open={purgeOpen} onClose={() => setPurgeOpen(false)} title={`Purge Data — ${purgeSite?.name || ""}`} testId="purge-modal">
        <div className="space-y-4">
          <div className="scada-panel p-3">
            <div className="scada-label mb-2">CURRENT DATA</div>
            {!purgeStats ? <div className="text-[#5A6573] font-mono-metric text-sm">Loading…</div> : (
              <div className="grid grid-cols-2 gap-2 text-xs font-mono-metric">
                <div>Readings: <span className="text-[#00E5FF]">{purgeStats.readings.toLocaleString()}</span></div>
                <div>Alarms: <span className="text-[#00E5FF]">{purgeStats.alarms.toLocaleString()}</span></div>
                <div>Active Alarms: <span className="text-[#FFB300]">{purgeStats.active_alarms}</span></div>
                <div>Tags: <span className="text-[#9BA4B5]">{purgeStats.tags}</span></div>
              </div>
            )}
          </div>

          <div className="scada-panel p-3 space-y-2">
            <div className="scada-label mb-1">WHAT TO PURGE</div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={purgeOpts.readings} onChange={(e) => setPurgeOpts({ ...purgeOpts, readings: e.target.checked })} data-testid="purge-readings" /> Sensor readings (history data)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={purgeOpts.alarms} onChange={(e) => setPurgeOpts({ ...purgeOpts, alarms: e.target.checked })} data-testid="purge-alarms" /> Alarm history (cleared alarms)</label>
            <label className="flex items-center gap-2 text-sm pl-6"><input type="checkbox" checked={purgeOpts.active_too} onChange={(e) => setPurgeOpts({ ...purgeOpts, active_too: e.target.checked })} disabled={!purgeOpts.alarms} data-testid="purge-active" /> Also purge ACTIVE alarms</label>
          </div>

          <Field label="Only data older than (leave blank = purge all)">
            <input type="datetime-local" value={purgeOpts.before} onChange={(e) => setPurgeOpts({ ...purgeOpts, before: e.target.value ? new Date(e.target.value).toISOString() : "" })} className={inputCls()} data-testid="purge-before" />
          </Field>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#2C3238]">
            <GhostBtn onClick={() => setPurgeOpen(false)}>Cancel</GhostBtn>
            <button onClick={doPurge} disabled={purgeBusy || (!purgeOpts.readings && !purgeOpts.alarms)} data-testid="purge-confirm"
              className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 px-4 py-2 font-mono-metric uppercase tracking-wider text-xs disabled:opacity-50 flex items-center gap-2">
              <Trash2 size={12} /> {purgeBusy ? "PURGING…" : "PURGE NOW"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
